import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AppIcon,
  DeepDive,
  DeepDiveCard,
  DEEP_DIVE_CARD_MAX_WIDTH,
  PageHeader,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import {
  Autocomplete,
  Box,
  Button,
  SxProps,
  Theme,
  Typography,
  Divider,
  TextField,
} from '@mui/material';

import { DeepDivesContent, ReviewCardConfig } from '../molecules/DeepDivesContent';
import { DeepDivesTabFilter, CATEGORY_TABS } from '../molecules/DeepDivesTabFilter';

const STRINGS = appStrings.modules.deepDives;
const REVIEW_CARD_STRINGS = STRINGS.reviewCards;

const REVIEW_CARDS_BY_CATEGORY: Record<string, ReviewCardConfig> = {
  'Test Prep': {
    profileImage: '/profile-pics/oliver.png',
    ...REVIEW_CARD_STRINGS.testPrep,
  },
  'Career Exploration': {
    profileImage: '/profile-pics/nesha.png',
    ...REVIEW_CARD_STRINGS.careerExploration,
  },
  'Internships & Professional Skills': {
    profileImage: '/profile-pics/jaron.jpeg',
    ...REVIEW_CARD_STRINGS.internshipsProfessionalSkills,
  },
};

type DeepDivesPageProps = Readonly<{
  deepDives: DeepDive[];
}>;

export const DeepDivesPage = ({ deepDives }: DeepDivesPageProps) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const handleMeetOurCoaches = () => {
    navigate(APP_ROUTE.COACHES);
  };

  // Popular deep dives
  const popularDeepDives = useMemo(() => {
    return deepDives.filter((dd) => {
      const isPopular = dd.is_popular;
      const matchesSearch = !searchTerm || dd.name.toLowerCase().includes(searchTerm.toLowerCase());

      return isPopular && matchesSearch;
    });
  }, [deepDives, searchTerm]);

  // Filtered deep dives for "Available Deep Dives" section
  const filteredDeepDives = useMemo(() => {
    return deepDives.filter((dd) => {
      const matchesSearch = !searchTerm || dd.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === 'all' || dd.tags?.some((tag) => tag.code === selectedCategory);

      return matchesSearch && matchesCategory;
    });
  }, [deepDives, searchTerm, selectedCategory]);

  const handleCardClick = (deepDive: DeepDive) => {
    navigate(`${APP_ROUTE.DEEP_DIVES}/${deepDive.id}`);
  };

  return (
    <Box sx={containerSx}>
      <Box sx={contentContainerSx}>
        <PageHeader
          title={STRINGS.header.title}
          subtitle={STRINGS.header.subtitle}
          maxWidth={null}
          showBorder={true}
          rightComponents={[
            <Button
              key="coaches"
              id="deep-dives-meet-coaches-button"
              hierarchy="Secondary color"
              size="md"
              startIcon={<AppIcon iconName="Hand" diameter="20px" color="Brand/500" />}
              onClick={handleMeetOurCoaches}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {STRINGS.header.meetOurCoaches}
            </Button>,
            <Autocomplete
              key="search"
              options={deepDives}
              getOptionLabel={(option) => option.name}
              inputValue={searchTerm}
              onInputChange={(_, value) => setSearchTerm(value)}
              onChange={(_, value) => {
                if (value) {
                  handleCardClick(value);
                }
              }}
              popupIcon={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="deep-dives-search-input"
                  variant="outlined"
                  placeholder={STRINGS.header.searchPlaceholder}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <AppIcon iconName="SearchLg" color="Gray/500" diameter="20px" />
                    ),
                  }}
                />
              )}
              sx={autocompleteContainerSx}
              slotProps={{
                paper: { sx: autocompletePaperSx },
              }}
            />,
          ]}
        />

        {/* Most Popular section - above filter, not affected by category filter */}
        {popularDeepDives.length > 0 && (
          <Box sx={sectionSx}>
            <Typography variant="text-md-bold" sx={sectionTitleSx}>
              {STRINGS.categories[0]}
            </Typography>
            <Box sx={gridSx}>
              {popularDeepDives.map((deepDive) => (
                <Box key={deepDive.id} sx={cardWrapperSx}>
                  <DeepDiveCard
                    deepDive={deepDive}
                    onCardClick={handleCardClick}
                    onCtaClick={handleCardClick}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <DeepDivesTabFilter
          count={filteredDeepDives.length}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          categories={CATEGORY_TABS}
        />

        <Box sx={{ bgcolor: ({ backrs }) => backrs.colors['Beige/950'] }}>
          <Divider />
        </Box>
        <DeepDivesContent
          deepDives={filteredDeepDives}
          reviewCardsByCategory={REVIEW_CARDS_BY_CATEGORY}
        />
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  alignSelf: 'center',
  gap: backrs.spacing['spacing-2xl'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  my: backrs.spacing['spacing-2xl'],
});

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, ${DEEP_DIVE_CARD_MAX_WIDTH}px)`,
  gap: backrs.spacing['spacing-4xl'],
  mt: backrs.spacing['spacing-lg'],
});

const cardWrapperSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
};

const autocompleteContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '253px',
  maxWidth: '100%',
  backgroundColor: backrs.colors['Colors/Base/White'],
  '& .MuiInputBase-root': {
    height: '42px',
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
    '&:hover': {
      border: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
      boxShadow: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

const autocompletePaperSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-lg'],
  boxShadow: backrs.shadows['shadow-lg'],
  mt: backrs.spacing['spacing-xs'],
});
